import { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { redirect as reduxFirstRouterRedirect, push } from 'redux-first-router';
import type { LocationState, Action } from 'redux-first-router';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { preFetchCompany as preFetchCompanyRoutine } from '@/pages/CompanyReport/redux/routines';
import type { SortBy } from './useSortByOptions';
import { getPageFromString } from '@/utilities/getPageFromString';
import { ROUTE_CREATE_CRITERIA } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { checkSupportedMarket } from '@/utilities/markets';
import { checkSupportedIndustry } from '@/utilities/industries';

interface Location {
  market?: PrimaryCountryISOElseGlobal;
  industry?: string | 'any';
  sortBy?: SortBy;
  pageQuery: number;
}

interface ReduxState extends Location {
  redirect: (routerAction: Action) => void;
  preFetchCompany: (id: string) => void;
  onAdvancedFilters: (market: string, name: string, industry?: string) => void;
}

export default function useReduxStateForStocks(): ReduxState {
  const { market, industry, sortBy, pageQuery } = useSelector<
    {
      location: LocationState;
    },
    Location
  >((state) => {
    const { market, industry, sortBy } = state.location.payload;
    const query = state.location.query;
    const safePage = getPageFromString(query?.page);
    return {
      market: checkSupportedMarket(market),
      industry: checkSupportedIndustry(industry),
      sortBy,
      pageQuery: safePage < 1 ? 1 : Math.round(safePage),
    };
  }, shallowEqual);

  const dispatch = useDispatch();

  const redirect = useCallback(
    (routerAction: Action) => {
      dispatch(reduxFirstRouterRedirect(routerAction));
    },
    [dispatch]
  );

  const preFetchCompany = useCallback(
    (companyId?: string) => {
      dispatch(preFetchCompanyRoutine.trigger({ id: companyId }));
    },
    [dispatch]
  );

  const onAdvancedFilters = useCallback(
    (marketISO: string, industrySlug?: string) => {
      const safeIndustrySlug =
        industrySlug !== 'any' ? industrySlug : undefined;
      const path = getPath({
        type: ROUTE_CREATE_CRITERIA,
        payload: { marketISO, industrySlug: safeIndustrySlug },
      });
      push(path);
    },
    []
  );

  return {
    market,
    industry,
    sortBy,
    pageQuery,
    redirect,
    preFetchCompany,
    onAdvancedFilters,
  };
}
